import { openSupportTask } from '../../services/supportTaskNavigation';
import React, { useState, useEffect } from 'react';
import {
  Bell,
  Check,
  ExternalLink,
  Filter,
  RefreshCw,
  Users,
  Calendar,
  Clock,
  CreditCard,
  TrendingUp,
  FileText,
  Mail
} from 'lucide-react';
import { Notification } from '../../services/notificationService';
import { useNotifications } from '../../hooks/useNotifications';

// User-friendly labels for notification codes
const getNotificationLabel = (code: string): string => {
  const labels: Record<string, string> = {
    // Leads y Clientes
    'LEAD.NEW': 'Nuevo Lead',
    'LEAD.ASSIGN': 'Lead Asignado',
    'LEAD.CONVERTED': 'Lead Convertido',
    'CONTACT.CREATED': 'Cliente Creado',
    'COMPANY.CREATED': 'Empresa Creada',
    
    // Reuniones y Actividades
    'MEETING.SCHEDULED': 'Reunión Programada',
    'MEETING.APPROVED': 'Reunión Aprobada',
    'MEETING.CANCELLED': 'Reunión Cancelada',
    'MEETING.APPROVAL_REQUIRED': 'Reunión Pendiente de Aprobación',
    'ACTIVITY.CREATED': 'Actividad Registrada',
    
    // Formularios
    'FORM.SUBMISSION': 'Formulario Recibido',
    'FORM.RESPONSE': 'Respuesta de Formulario',
    
    // Plantilla y Horas
    'TIMESHEET.SUBMIT': 'Horas Enviadas',
    'TIMESHEET.APPROVE': 'Horas Aprobadas',
    'TIMESHEET.REJECT': 'Horas Rechazadas',
    'INVOICE.COLLAB.UPLOAD': 'Factura Subida',
    
    // Pagos y Finanzas
    'PAYMENT.SCHEDULED': 'Pago Programado',
    'PAYMENT.DUE': 'Pago Pendiente',
    'PAYMENT.SENT': 'Pago Ejecutado',
    'INVOICE.OVERDUE': 'Factura Vencida',
    
    // Qonto (Ingresos)
    'QONTO.TX.INCOME': 'Ingreso Detectado',
    'QONTO.INCOME.UNASSIGNED': 'Ingreso Sin Asignar',
    'QONTO.BANK.TX_IN': 'Transferencia Recibida',
    'QONTO.BANK.TX_OUT': 'Transferencia Enviada',
    
    // Tareas y Proyectos
    'TASK.DUE_TODAY': 'Tarea Vence Hoy',
    'PROJECT.MILESTONE.DUE': 'Hito Próximo',
    'PROJECT.LATE': 'Proyecto Retrasado',
    
    // Contratos y Documentos
    'CONTRACT.RENEW.SOON': 'Contrato por Renovar',
    'DOC.SIGNED': 'Documento Firmado',
    
    // Sistema y Usuarios
    'USER.CREATED': 'Usuario Creado',
    'SYSTEM.UPDATE': 'Actualización del Sistema',
    
    // Resúmenes
    'DIGEST.DAILY.ADMIN': 'Resumen Diario',
    'DIGEST.WEEKLY.PM': 'Resumen Semanal'
  };
  
  return labels[code] || code;
};

const NotificationsList: React.FC = () => {
  const {
    notifications,
    unreadCount,
    loadNotifications,
    markAsRead,
    markAllAsRead
  } = useNotifications();
  
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  const handleRefresh = async () => {
    setLoading(true);
    await loadNotifications();
    setLoading(false);
  };

  const handleMarkAllAsRead = async () => {
    try {
      if (unreadCount === 0) {
        alert('No hay notificaciones sin leer');
        return;
      }

      if (window.confirm(`¿Marcar ${unreadCount} notificaciones como leídas?`)) {
        await markAllAsRead();
        console.log(`✅ ${unreadCount} notificaciones marcadas como leídas`);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      alert('Error al marcar las notificaciones como leídas');
    }
  };

  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.read_at) return;
    await markAsRead(notification.id);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      handleMarkAsRead(notification);
    }

    if (notification.code === 'SUPPORT.CASE_CREATED') openSupportTask(notification.data);
  };

  const getNotificationIcon = (code: string) => {
    if (code.startsWith('LEAD.')) return Users;
    if (code.startsWith('MEET.') || code.startsWith('MEETING.')) return Calendar;
    if (code.startsWith('TIMESHEET.') || code.startsWith('INVOICE.')) return Clock;
    if (code.startsWith('PAYMENT.')) return CreditCard;
    if (code.startsWith('QONTO.')) return TrendingUp;
    if (code.startsWith('CONTRACT.') || code.startsWith('DOC.')) return FileText;
    if (code.startsWith('DIGEST.')) return Mail;
    return Bell;
  };

  const getNotificationColor = (severity: string, isRead: boolean) => {
    if (isRead) return 'text-gray-600 bg-gray-100';

    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'warn': return 'text-orange-600 bg-orange-100';
      case 'info': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'warn': return 'bg-orange-100 text-orange-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const filteredNotifications = notifications.filter(notification => {
    if (typeFilter !== 'all' && !notification.code.startsWith(typeFilter)) return false;
    return true;
  });

  const notificationTypes = [
    { value: 'all', label: 'Todos los tipos' },
    { value: 'LEAD.', label: 'Leads' },
    { value: 'MEET.', label: 'Reuniones' },
    { value: 'TIMESHEET.', label: 'Horas' },
    { value: 'PAYMENT.', label: 'Pagos' },
    { value: 'QONTO.', label: 'Qonto' },
    { value: 'DIGEST.', label: 'Resúmenes' }
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Todas las Notificaciones</h2>
          <p className="text-gray-600">
            {unreadCount > 0 ? `${unreadCount} notificaciones sin leer` : 'Todas las notificaciones están al día'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Check className="w-4 h-4" />
              <span>Marcar Todas Leídas ({unreadCount})</span>
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span>{loading ? 'Actualizando...' : 'Actualizar'}</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Estado:</span>
            <div className="flex space-x-2">
              {[
                { value: 'all', label: 'Sin leer' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setFilter(option.value as any)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    filter === option.value
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Tipo:</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {notificationTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredNotifications.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => {
              const Icon = getNotificationIcon(notification.code);
              const colorClass = getNotificationColor(notification.severity, !!notification.read_at);
              
              return (
                <div
                  key={notification.id}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors bg-blue-50 border-l-4 border-blue-500"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getSeverityColor(notification.severity)}`}>
                            {notification.severity === 'critical' ? 'Crítico' :
                             notification.severity === 'warn' ? 'Aviso' :
                             notification.severity === 'info' ? 'Info' : notification.severity}
                          </span>
                          <span className="text-xs text-gray-500">
                            {getNotificationLabel(notification.code)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(notification.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">
                        {notification.body}
                      </p>
                      
                      {/* Payload info */}
                      {notification.data && Object.keys(notification.data).length > 0 && (
                        <div className="bg-gray-50 rounded p-2 mb-3">
                          <p className="text-xs text-gray-500 mb-1">Datos adicionales:</p>
                          <div className="text-xs text-gray-700">
                            {Object.entries(notification.data).map(([key, value]) => (
                              <span key={key} className="mr-3">
                                <strong>{key}:</strong> {String(value)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification);
                          }}
                          className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <Check className="w-3 h-3" />
                          <span>Marcar leída</span>
                        </button>

                        {notification.deep_link && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                            className="flex items-center space-x-1 text-xs text-green-600 hover:text-green-800"
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>Ir a {notification.deep_link.replace('/', '')}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay notificaciones sin leer
            </h3>
            <p className="text-gray-500">
              Las notificaciones aparecerán aquí cuando se generen eventos en el sistema
            </p>
          </div>
        )}
      </div>

      {/* Summary */}
      {notifications.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Mostrando {filteredNotifications.length} de {notifications.length} notificaciones
          {unreadCount > 0 && ` • ${unreadCount} sin leer`}
        </div>
      )}
    </div>
  );
};

export default NotificationsList;